export const REDIRECT_FOLLOWED = 'REDIRECT_FOLLOWED'

const REDIRECT_STATUSES = new Set([301, 302, 303, 307, 308])
const MAX_REDIRECTS = 5

type RuntimePublicConfig = {
    API_URL?: string
    baseURL?: string
}

export function isRedirectFollowedError(error: unknown): boolean {
    const e = error as { name?: string; message?: string }

    return e?.name === 'RedirectFollowedError' || e?.message === REDIRECT_FOLLOWED
}

export function resolveRedirectPath(location: string, config: RuntimePublicConfig): string {
    const apiBase = (config.API_URL || '').replace(/\/$/, '')
    const siteBase = (config.baseURL || '').replace(/\/$/, '')

    let url: URL

    try {
        url = new URL(location, apiBase || siteBase || 'http://localhost')
    } catch {
        return location.startsWith('/') ? location : `/${location}`
    }

    if (siteBase) {
        try {
            const siteOrigin = new URL(siteBase).origin

            if (url.origin === siteOrigin) {
                return `${url.pathname}${url.search}${url.hash}`
            }
        } catch {
            // ignore invalid site base
        }
    }

    if (apiBase) {
        try {
            const apiOrigin = new URL(apiBase).origin
            const isApiRedirect = url.origin === apiOrigin || location.startsWith('/')

            if (isApiRedirect) {
                return mapApiPathToFrontPath(url.pathname, url.search, url.hash)
            }
        } catch {
            // ignore invalid api base
        }
    }

    return `${url.pathname}${url.search}${url.hash}`
}

function mapApiPathToFrontPath(pathname: string, search = '', hash = ''): string {
    const suffix = `${search}${hash}`

    if (pathname === '/page' || pathname === '/page/') {
        return `/${suffix}`
    }

    const pageMatch = pathname.match(/^\/page\/(.+)$/)

    if (pageMatch) {
        return `/${pageMatch[1]}${suffix}`
    }

    const productMatch = pathname.match(/^\/product\/(.+)$/)

    if (productMatch) {
        return `/shop/${productMatch[1]}${suffix}`
    }

    const blogMatch = pathname.match(/^\/blog\/(.+)$/)

    if (blogMatch) {
        return `/blogg/${blogMatch[1]}${suffix}`
    }

    if (pathname === '/collection' || pathname === '/collection/') {
        return `/collections${suffix}`
    }

    const collectionMatch = pathname.match(/^\/collection\/(.+)$/)

    if (collectionMatch) {
        return `/collections/${collectionMatch[1]}${suffix}`
    }

    return `${pathname}${suffix}`
}

export async function fetchApiWithRedirects(url: string, opts: Record<string, unknown> = {}, depth = 0, publicConfig?: RuntimePublicConfig): Promise<unknown> {
    if (depth >= MAX_REDIRECTS) {
        throw createError({statusCode: 508, statusMessage: 'Redirect loop'})
    }

    const config = publicConfig ?? useRuntimeConfig().public
    const method = String(opts.method ?? 'GET').toUpperCase()

    const response = await $fetch.raw(url, {
        ...opts,
        baseURL: (opts.baseURL as string | undefined) ?? config.API_URL,
        redirect: 'manual',
    })

    if (REDIRECT_STATUSES.has(response.status)) {
        const location = response.headers.get('location')

        if (!location) {
            throw createError({
                statusCode: response.status,
                statusMessage: 'Redirect without location',
            })
        }

        if (method === 'GET') {
            const path = resolveRedirectPath(location, config)

            if (import.meta.client) {
                window.location.href = path

                const error = new Error(REDIRECT_FOLLOWED)
                error.name = 'RedirectFollowedError'
                ;(error as { statusCode?: number }).statusCode = 499

                throw error
            }

            await navigateTo(path, {
                replace: true,
                redirectCode: response.status === 302 || response.status === 303 ? 302 : 301,
            })
        }

        return fetchApiWithRedirects(location, opts, depth + 1, config)
    }

    if (response.status >= 400) {
        throw createError({
            statusCode: response.status,
            statusMessage: response.statusText || 'Request failed',
            data: response._data,
        })
    }

    return response._data
}
